"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DESCRIPTION_REQUIRED_MESSAGE = exports.TICKET_CLOSURE_ERROR_CODES = exports.TICKET_CLOSURE_EVENT_TYPE_LABELS = exports.TICKET_CLOSURE_EVENT_TYPES = exports.changeTicketStateSchema = exports.MANUAL_TICKET_STATES = exports.discardTicketSchema = exports.resolveTicketSchema = exports.setTicketCorrectionSchema = exports.AUTO_CLOSE_ACTOR = exports.CLOSURE_NO_DETAILS_TEXT = exports.CORRECTION_RELEASE_MAX = exports.CLOSURE_SUBJECT_DESCRIPTION_MAX = exports.CORRECTION_TEXT_MAX = exports.CLOSURE_DESCRIPTION_MIN_LENGTH = void 0;
exports.manualStateTargets = manualStateTargets;
exports.resolveStatePath = resolveStatePath;
exports.canResolve = canResolve;
exports.canWriteCorrection = canWriteCorrection;
exports.canDiscard = canDiscard;
exports.canCloseOrReopen = canCloseOrReopen;
exports.isDescriptionMissing = isDescriptionMissing;
exports.citedDescription = citedDescription;
const zod_1 = require("zod");
const ticket_state_js_1 = require("./ticket-state.js");
/**
 * Correzione dello sviluppo, chiusura, scarto e riapertura del ticket (PRD-004 US-5, story #6,
 * DEC-002). Contratti fra l'API della console e la web app: chiavi in inglese, valori di dominio
 * in italiano.
 *
 * - **Correzione dello sviluppo**: blocco distinto dal thread (autore, momento, versione di
 *   rilascio, testo) che resta nel registro dopo la chiusura. Compilarlo è volontario (DEC-004).
 * - **«Invia e chiudi»** (`POST /tickets/:code/resolve`): l'unica strada verso `Risolto`, sempre con
 *   l'email di chiusura a chi ha segnalato (DEC-002 regola 2).
 * - **«Scarta»** (`POST /tickets/:code/discard`): verso `Scartato` con un motivo, senza email.
 * - **«Chiudi»** e **«Riapri»** (`POST /tickets/:code/close`, `/reopen`): solo dal responsabile.
 * - **Cambio di stato manuale** (`PUT /tickets/:code/state`): solo fra `In lavorazione` e
 *   `In attesa dell'utente`; mai verso `Risolto`, `Chiuso` o `Scartato`, che hanno le loro azioni.
 */
/** Lunghezza minima della descrizione con la regola «Descrizione obbligatoria alla chiusura» attiva. */
exports.CLOSURE_DESCRIPTION_MIN_LENGTH = 10;
/** Lunghezza massima del testo della correzione (entra nel testo dell'email di chiusura). */
exports.CORRECTION_TEXT_MAX = 5_000;
/** Lunghezza massima della descrizione nell'oggetto dell'email di chiusura: oltre, troncata con «…». */
exports.CLOSURE_SUBJECT_DESCRIPTION_MAX = 150;
/** Lunghezza massima della versione di rilascio («2.14.3 · in produzione dal 18/09»). */
exports.CORRECTION_RELEASE_MAX = 120;
/**
 * Testo che prende il posto di `{descrizione}` nell'email di chiusura quando nessuno ha scritto la
 * correzione: dichiara l'assenza invece di tacerla (PRD-004 US-5).
 */
exports.CLOSURE_NO_DETAILS_TEXT = 'Non ci sono dettagli sulla correzione: chi ha lavorato la segnalazione non li ha scritti.';
/** Autore degli eventi della chiusura automatica da `Risolto` a `Chiuso`: nessuna persona, è la regola di coda. */
exports.AUTO_CLOSE_ACTOR = 'Chiusura automatica';
/**
 * Codice del ticket originale di un duplicato: lo stesso formato di `TICKET_CODE_PATTERN` (`tickets.ts`),
 * ripetuto qui per non creare un import circolare fra i due moduli.
 */
const ORIGINAL_CODE_PATTERN = /^[A-Z]{2,5}-\d{4}-\d{4,}$/;
/** Testo della correzione: testo semplice (DEC-008), a capo conservati, niente NUL. */
const correctionText = (required) => zod_1.z
    .string({ error: 'Descrizione: campo obbligatorio' })
    .trim()
    .min(required ? 1 : 0, { error: 'Scrivi la descrizione della correzione', abort: true })
    .max(exports.CORRECTION_TEXT_MAX, { error: `Descrizione troppo lunga (massimo ${exports.CORRECTION_TEXT_MAX} caratteri)` })
    .refine((value) => !value.includes('\0'), { error: 'Il testo non può contenere caratteri di controllo' });
/** Versione di rilascio: una riga; vuota = assente (`null`). */
const releaseSchema = zod_1.z
    .string({ error: 'Versione di rilascio non valida' })
    .trim()
    .max(exports.CORRECTION_RELEASE_MAX, {
    error: `Versione di rilascio troppo lunga (massimo ${exports.CORRECTION_RELEASE_MAX} caratteri)`,
})
    .refine((value) => !/\p{Cc}/u.test(value), { error: 'La versione di rilascio sta su una riga' })
    .nullish()
    .transform((value) => (value ? value : null));
const strict = {
    error: (issue) => issue.code === 'unrecognized_keys' ? `Campi non ammessi: ${(issue.keys ?? []).join(', ')}` : undefined,
};
/** Corpo di `PUT /tickets/:code/correction`: registra la correzione senza chiudere il ticket. */
exports.setTicketCorrectionSchema = zod_1.z.strictObject({ text: correctionText(true), release: releaseSchema }, strict);
/**
 * Corpo di `POST /tickets/:code/resolve` («Invia e chiudi»). `description` è la descrizione della
 * correzione per chi ha segnalato: vuota = si usa la correzione già registrata, se c'è; altrimenti
 * l'email dichiara che non ci sono dettagli. La lunghezza minima la decide la regola di coda
 * (`CLOSURE_DESCRIPTION_MIN_LENGTH`), applicata dall'API. Il rilascio accompagna una descrizione.
 */
exports.resolveTicketSchema = zod_1.z
    .strictObject({ description: correctionText(false).default(''), release: releaseSchema }, strict)
    .refine((value) => value.release === null || value.description.length > 0, {
    error: 'La versione di rilascio accompagna la descrizione della correzione: scrivi anche cosa è stato fatto',
    path: ['release'],
});
/** Corpo di `POST /tickets/:code/discard` («Scarta»): motivo e, per `duplicato`, il ticket originale. */
exports.discardTicketSchema = zod_1.z
    .strictObject({
    reason: zod_1.z.enum(ticket_state_js_1.DISCARD_REASONS, { error: 'Motivo non valido: duplicato, spam o non pertinente' }),
    original: zod_1.z
        .string({ error: 'Codice del ticket originale non valido' })
        .trim()
        .toUpperCase()
        .nullish()
        .transform((value) => (value ? value : null))
        .refine((value) => value === null || ORIGINAL_CODE_PATTERN.test(value), {
        error: 'Codice del ticket originale non valido (es. EC-2026-0001)',
    }),
}, strict)
    .superRefine((value, ctx) => {
    if (value.reason === 'duplicato' && value.original === null) {
        ctx.addIssue({ code: 'custom', path: ['original'], message: 'Indica il codice del ticket originale' });
    }
    if (value.reason !== 'duplicato' && value.original !== null) {
        ctx.addIssue({
            code: 'custom',
            path: ['original'],
            message: 'Il ticket originale si indica solo per un duplicato',
        });
    }
});
/**
 * Stati raggiungibili dal cambio di stato manuale: solo fra `In lavorazione` e `In attesa
 * dell'utente` (DEC-002, nota di implementazione del 03/10/2026, che precisa l'opzione F).
 * `Risolto`, `Chiuso` e `Scartato` hanno ciascuno la propria azione.
 */
exports.MANUAL_TICKET_STATES = ['in_lavorazione', 'in_attesa_utente'];
/** Corpo di `PUT /tickets/:code/state`. */
exports.changeTicketStateSchema = zod_1.z.strictObject({
    state: zod_1.z.enum(exports.MANUAL_TICKET_STATES, {
        error: 'Stato non ammesso: dal cambio manuale solo «In lavorazione» o «In attesa dell’utente»',
    }),
}, strict);
/**
 * Stati a cui il cambio manuale porta il ticket da `state`. Da `Nuovo` nessuno: si esce con
 * l'assegnazione (#4), che porta a `In lavorazione` con un responsabile (DEC-004), o con la risposta.
 * Da `Risolto` nessuno: la riapertura è «Riapri» del responsabile o la replica del segnalante, e
 * conta nelle statistiche.
 */
function manualStateTargets(state) {
    switch (state) {
        case 'in_attesa_utente':
            return ['in_lavorazione'];
        case 'in_lavorazione':
            return ['in_attesa_utente'];
        case 'nuovo':
        case 'risolto':
        case 'chiuso':
        case 'scartato':
            return [];
    }
}
/**
 * Cambi di stato che «Invia e chiudi» applica, in ordine, dallo stato corrente (DEC-002): da
 * `nuovo` passa per `in_lavorazione`, così ogni transizione è un evento del thread.
 *
 * @returns le transizioni, `null` se da questo stato non si chiude (già risolto, o terminale)
 */
function resolveStatePath(state) {
    switch (state) {
        case 'nuovo':
            return ['in_lavorazione', 'risolto'];
        case 'in_lavorazione':
        case 'in_attesa_utente':
            return ['risolto'];
        case 'risolto':
        case 'chiuso':
        case 'scartato':
            return null;
    }
}
/** Vero se da questo stato si può usare «Invia e chiudi». */
function canResolve(state) {
    return resolveStatePath(state) !== null;
}
/** Vero se si può registrare la correzione: prima della chiusura (dopo, con «Riapri»). */
function canWriteCorrection(state) {
    return canResolve(state);
}
/** «Scarta» da qualunque stato non terminale (DEC-002 regola 3). */
function canDiscard(state) {
    return !(0, ticket_state_js_1.isTerminal)(state);
}
/** «Chiudi» (verso `Chiuso`) e «Riapri» (verso `In lavorazione`) partono solo da `Risolto`. */
function canCloseOrReopen(state) {
    return state === 'risolto';
}
/** Tipi di evento della story #6: `details` = `{ membro, correzione, rilascio }`. */
exports.TICKET_CLOSURE_EVENT_TYPES = ['correzione_registrata'];
exports.TICKET_CLOSURE_EVENT_TYPE_LABELS = {
    correzione_registrata: 'Correzione registrata',
};
/** Codici di errore della chiusura, nel campo `code` del corpo di errore dell'API. */
exports.TICKET_CLOSURE_ERROR_CODES = {
    /** 409: «Invia e chiudi» su un ticket già `Risolto`, `Chiuso` o `Scartato`. */
    resolveNotAllowed: 'tickets.chiusura_non_ammessa',
    /** 409: correzione su un ticket `Risolto` (prima «Riapri»), `Chiuso` o `Scartato`. */
    correctionNotAllowed: 'tickets.correzione_non_ammessa',
    /** 422: regola «Descrizione obbligatoria alla chiusura» attiva e descrizione assente o corta. */
    descriptionRequired: 'tickets.descrizione_obbligatoria',
    /** 403: «Chiudi» e «Riapri» sono del responsabile del ticket, di nessun altro (DEC-002 regole 4 e 5). */
    responsibleOnly: 'tickets.solo_responsabile',
    /** 422: lo scarto come duplicato cita un ticket che non esiste, o il ticket stesso. */
    originalInvalid: 'tickets.originale_non_valido',
};
/**
 * Vero se la regola «Descrizione obbligatoria alla chiusura» blocca la chiusura con questa
 * descrizione (già senza spazi ai bordi). Le impostazioni si leggono al momento dell'uso.
 */
function isDescriptionMissing(description, settings) {
    return settings.requireDescriptionOnClose && description.length < exports.CLOSURE_DESCRIPTION_MIN_LENGTH;
}
/**
 * La descrizione che «Invia e chiudi» cita: quella scritta alla chiusura (senza spazi ai bordi) o,
 * se è vuota, la correzione già registrata. È su questa che si applica la regola della descrizione,
 * nell'API e nel composer.
 */
function citedDescription(written, current) {
    return written.trim() || current?.text || '';
}
/** Messaggio della regola: aiuto nel composer e testo dell'errore dell'API. */
exports.DESCRIPTION_REQUIRED_MESSAGE = `Scrivi cosa è stato corretto (almeno ${exports.CLOSURE_DESCRIPTION_MIN_LENGTH} caratteri): la regola «Descrizione obbligatoria alla chiusura» è attiva.`;
//# sourceMappingURL=ticket-closure.js.map